import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { SortType } from "../../../../../models/SortType";
import { OrderPipelineRow } from "../../../../../models/Dashboard/OrderTracking/OrderTrackingAnalytics";
import { formatDateWithoutTime } from "../../../../../views/components/common/functions/formatDate";

interface OrderPipelineTableProps {
  rows: OrderPipelineRow[];
  page_no: number;
  limit: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onSortChange: (column: string, sortType: SortType) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
}

type ColumnDef = {
  key: string;
  label: string;
  width: number;
  align: "left" | "right" | "center";
  wrap?: boolean;
  headerLines?: string[];
};

type ColumnGroup = {
  label: string;
  bgColor: string;
  columns: ColumnDef[];
};

type ColumnWithMeta = ColumnDef & {
  isSectionEnd: boolean;
};

const SECTION_BORDER = "2px solid #B8C4DC";

const columnGroups: ColumnGroup[] = [
  {
    label: "Order",
    bgColor: "#E8EDF8",
    columns: [
      { key: "order_id", label: "Order ID", width: 92, align: "center" },
      { key: "order_created_date", label: "Order Created", width: 118, align: "center" },
      { key: "project_created_date", label: "Project Created", width: 126, align: "center" },
      { key: "order_type", label: "Order Type", width: 110, align: "left" },
      { key: "customer_name", label: "Customer", width: 150, align: "left", wrap: true },
      { key: "sales_poc", label: "Sales POC", width: 120, align: "left" },
      { key: "pm_poc", label: "PM POC", width: 120, align: "left" },
      { key: "product_id", label: "Product ID", width: 92, align: "center" },
      { key: "product_name", label: "Product", width: 200, align: "left", wrap: true },
      { key: "ordered_quantity", label: "Ordered Qty", width: 102, align: "center" },
      { key: "customisation_required", label: "Customisation", width: 118, align: "center" },
    ],
  },
  {
    label: "Mockup",
    bgColor: "#EDE8F8",
    columns: [
      { key: "all_mockups_approved", label: "Approved", width: 88, align: "center" },
      { key: "last_mockup_approved_date", label: "Last Approved", width: 118, align: "center" },
    ],
  },
  {
    label: "PR / PO",
    bgColor: "#E8F4ED",
    columns: [
      { key: "last_pr_date", label: "Last PR Date", width: 110, align: "center" },
      { key: "pr_quantity", label: "PR Qty", width: 80, align: "center" },
      { key: "po_quantity", label: "PO Qty", width: 80, align: "center" },
    ],
  },
  {
    label: "Raw Inward",
    bgColor: "#F4F0E8",
    columns: [
      { key: "first_raw_inward_date", label: "First Inward", width: 110, align: "center" },
      { key: "last_raw_inward_date", label: "Last Inward", width: 110, align: "center" },
      { key: "ri_total_received_quantity", label: "Received Qty", width: 102, align: "center" },
      { key: "raw_inward_completed", label: "Completed", width: 88, align: "center" },
    ],
  },
  {
    label: "Print Outward",
    bgColor: "#F4E8F0",
    columns: [
      { key: "po_total_quantity_sent", label: "Sent Qty", width: 88, align: "center" },
      { key: "printers", label: "Printers", width: 140, align: "left", wrap: true },
    ],
  },
  {
    label: "Print Inward",
    bgColor: "#E8F0F4",
    columns: [
      { key: "last_print_inward_date", label: "Last Inward", width: 110, align: "center" },
      { key: "pi_total_received_quantity", label: "Received Qty", width: 102, align: "center" },
      { key: "print_inward_completed", label: "Completed", width: 88, align: "center" },
    ],
  },
  {
    label: "Dispatch",
    bgColor: "#F0E8F4",
    columns: [
      { key: "shipments_count", label: "Shipments", width: 92, align: "center" },
      { key: "first_shipped_date", label: "First Shipped", width: 110, align: "center" },
    ],
  },
  {
    label: "TAT (hrs)",
    bgColor: "#E8F4F0",
    columns: [
      {
        key: "tat_project_approval_hours",
        label: "Project Approval",
        headerLines: ["Project", "Approval"],
        width: 112,
        align: "center",
      },
      {
        key: "tat_mockup_approval_hours",
        label: "Mockup Approval",
        headerLines: ["Mockup", "Approval"],
        width: 112,
        align: "center",
      },
      {
        key: "tat_pr_from_project_hours",
        label: "PR from Project",
        headerLines: ["PR from", "Project"],
        width: 112,
        align: "center",
      },
      {
        key: "tat_raw_inward_from_project_hours",
        label: "Raw Inward from Project",
        headerLines: ["Raw Inward", "from Project"],
        width: 128,
        align: "center",
      },
      {
        key: "tat_print_inward_from_project_hours",
        label: "Print Inward from Project",
        headerLines: ["Print Inward", "from Project"],
        width: 128,
        align: "center",
      },
      {
        key: "tat_print_inward_from_raw_inward_hours",
        label: "Print Inward from Raw Inward",
        headerLines: ["Print Inward", "from Raw Inward"],
        width: 144,
        align: "center",
      },
      {
        key: "tat_total_project_to_ship_hours",
        label: "Project to Ship",
        headerLines: ["Project", "to Ship"],
        width: 112,
        align: "center",
      },
    ],
  },
];

const columnsWithMeta: ColumnWithMeta[] = columnGroups.flatMap((group, groupIndex) =>
  group.columns.map((column, columnIndex) => ({
    ...column,
    isSectionEnd:
      columnIndex === group.columns.length - 1 &&
      groupIndex < columnGroups.length - 1,
  })),
);

const TABLE_MIN_WIDTH = columnsWithMeta.reduce((sum, col) => sum + col.width, 0);

const sectionBorderSx = (isSectionEnd: boolean) =>
  isSectionEnd ? { borderRight: SECTION_BORDER } : {};

const netReceivedQty = (received: number, rejected: number) =>
  received - rejected;

const headerCellSx = {
  color: "#1E2A44",
  fontSize: "0.74rem",
  fontWeight: 700,
  py: 1,
  px: 1.1,
  borderBottom: "1px solid #E2E8F4",
};

const columnHeaderCellSx = {
  ...headerCellSx,
  lineHeight: 1.25,
  verticalAlign: "middle" as const,
  boxSizing: "border-box" as const,
  minHeight: 52,
  px: 1.25,
};

const bodyCellSx = {
  fontSize: "0.76rem",
  px: 1.1,
  py: 1,
  verticalAlign: "middle",
  borderColor: "#EEF1F7",
  color: "#1E2A44",
};

const YesNoChip: React.FC<{
  value: number;
  yesColor?: string;
  yesBg?: string;
}> = ({ value, yesColor = "#166534", yesBg = "#DCFCE7" }) => (
  <Chip
    label={value === 1 ? "Yes" : "No"}
    size="small"
    sx={{
      height: 20,
      fontSize: "0.65rem",
      fontWeight: 600,
      bgcolor: value === 1 ? yesBg : "#F1F5F9",
      color: value === 1 ? yesColor : "#64748B",
    }}
  />
);

const OrderPipelineTable: React.FC<OrderPipelineTableProps> = ({
  rows,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const orderGroupIndex = useMemo(() => {
    const map = new Map<number, number>();
    let group = 0;
    let lastOrderId: number | null = null;
    rows.forEach((row) => {
      if (row.order_id !== lastOrderId) {
        group += 1;
        lastOrderId = row.order_id;
      }
      map.set(row.order_id * 100000 + row.product_id, group);
    });
    return map;
  }, [rows]);

  const formatDateValue = (value: string | null) => {
    if (!value) return "-";
    const formatted = formatDateWithoutTime(value);
    return formatted || "-";
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↕";
  };

  const renderSortableHeader = (column: ColumnDef) => (
    <Box
      title={column.label}
      onClick={() =>
        onSortChange(
          column.key,
          sortBy === column.key && sortType === SortType.DESC
            ? SortType.ASC
            : SortType.DESC,
        )
      }
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.35,
        width: "100%",
        maxWidth: "100%",
        cursor: "pointer",
        justifyContent:
          column.align === "center"
            ? "center"
            : column.align === "right"
              ? "flex-end"
              : "flex-start",
        color: sortBy === column.key ? "#6455ED" : "#1E2A44",
        "&:hover": { color: "#6455ED" },
      }}
    >
      <Box
        component="span"
        sx={{
          display: "flex",
          flexDirection: column.headerLines ? "column" : "row",
          alignItems:
            column.align === "center"
              ? "center"
              : column.align === "right"
                ? "flex-end"
                : "flex-start",
          lineHeight: 1.25,
          fontSize: column.headerLines ? "0.7rem" : "0.74rem",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {column.headerLines
          ? column.headerLines.map((line) => (
            <Box component="span" key={line} sx={{ whiteSpace: "nowrap" }}>
              {line}
            </Box>
          ))
          : column.label}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: "0.68rem",
          flexShrink: 0,
          lineHeight: 1,
          opacity: sortBy === column.key ? 1 : 0.55,
        }}
      >
        {renderSortIcon(column.key)}
      </Box>
    </Box>
  );

  const getCellValue = (row: OrderPipelineRow, key: string) => {
    switch (key) {
      case "order_id":
        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Link
              href={`/projects/${row.order_id}`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                fontWeight: 700,
                color: "#6455ED",
                fontSize: "0.76rem",
                whiteSpace: "nowrap",
              }}
            >
              {row.order_id}
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Link>
          </Box>
        );
      case "order_created_date":
        return formatDateValue(row.order_created_date);
      case "project_created_date":
        return formatDateValue(row.project_created_date);
      case "order_type":
        return row.order_type || "-";
      case "customer_name":
        return row.customer_name || "-";
      case "sales_poc":
        return row.sales_poc || "-";
      case "pm_poc":
        return row.pm_poc || "-";
      case "product_id":
        return row.product_id;
      case "product_name":
        return row.product_name || "-";
      case "ordered_quantity":
        return row.ordered_quantity;
      case "customisation_required":
        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <YesNoChip
              value={row.customisation_required}
              yesColor="#7C3AED"
              yesBg="#F3E8FF"
            />
          </Box>
        );
      case "all_mockups_approved":
        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <YesNoChip
              value={row.all_mockups_approved}
              yesColor="#166534"
              yesBg="#DCFCE7"
            />
          </Box>
        );
      case "last_mockup_approved_date":
        return formatDateValue(row.last_mockup_approved_date);
      case "last_pr_date":
        return formatDateValue(row.last_pr_date);
      case "pr_quantity":
        return row.pr_quantity;
      case "po_quantity":
        return row.po_quantity;
      case "first_raw_inward_date":
        return formatDateValue(row.first_raw_inward_date);
      case "last_raw_inward_date":
        return formatDateValue(row.last_raw_inward_date);
      case "ri_total_received_quantity":
        return netReceivedQty(
          row.ri_total_received_quantity,
          row.ri_total_rejected_quantity,
        );
      case "raw_inward_completed":
        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <YesNoChip value={row.raw_inward_completed} />
          </Box>
        );
      case "po_total_quantity_sent":
        return row.po_total_quantity_sent;
      case "printers":
        return row.printers || "-";
      case "last_print_inward_date":
        return formatDateValue(row.last_print_inward_date);
      case "pi_total_received_quantity":
        return netReceivedQty(
          row.pi_total_received_quantity,
          row.pi_total_rejected_quantity,
        );
      case "print_inward_completed":
        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <YesNoChip value={row.print_inward_completed} />
          </Box>
        );
      case "shipments_count":
        return row.shipments_count;
      case "first_shipped_date":
        return formatDateValue(row.first_shipped_date);
      case "tat_project_approval_hours":
        return row.tat_project_approval_hours;
      case "tat_mockup_approval_hours":
        return row.tat_mockup_approval_hours;
      case "tat_pr_from_project_hours":
        return row.tat_pr_from_project_hours;
      case "tat_raw_inward_from_project_hours":
        return row.tat_raw_inward_from_project_hours;
      case "tat_print_inward_from_project_hours":
        return row.tat_print_inward_from_project_hours;
      case "tat_print_inward_from_raw_inward_hours":
        return row.tat_print_inward_from_raw_inward_hours;
      case "tat_total_project_to_ship_hours":
        return row.tat_total_project_to_ship_hours;
      default:
        return "-";
    }
  };

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 220,
          px: 3,
          py: 4,
          bgcolor: "#FBFCFF",
          borderRadius: 2,
          border: "1px dashed #DDE3F5",
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#1E2A44" }}>
          No records in this stage
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.75, color: "#5C6785", textAlign: "center" }}>
          Try another pipeline tab or adjust your date range and filters.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: "#E2E8F4",
        overflow: "hidden",
      }}
    >
      <TableContainer
        sx={{
          maxHeight: 640,
          overflowX: "auto",
          overflowY: "auto",
          width: "100%",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: TABLE_MIN_WIDTH,
            tableLayout: "fixed",
            width: TABLE_MIN_WIDTH,
          }}
        >
          <TableHead>
            <TableRow>
              {columnGroups.map((group, groupIndex) => (
                <TableCell
                  key={group.label}
                  colSpan={group.columns.length}
                  align="center"
                  sx={{
                    ...headerCellSx,
                    ...sectionBorderSx(groupIndex < columnGroups.length - 1),
                    bgcolor: group.bgColor,
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    borderBottom: "2px solid #D5DCEC",
                    py: 0.85,
                  }}
                >
                  {group.label}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {columnsWithMeta.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{
                    ...columnHeaderCellSx,
                    ...sectionBorderSx(column.isSectionEnd),
                    bgcolor: "#F4F6FB",
                    width: column.width,
                    minWidth: column.width,
                    textAlign: column.align,
                  }}
                >
                  {renderSortableHeader(column)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const groupKey = row.order_id * 100000 + row.product_id;
              const isOddGroup = (orderGroupIndex.get(groupKey) ?? 0) % 2 === 1;
              const rowBg = isOddGroup ? "#FFFFFF" : "#F8FAFD";

              return (
                <TableRow
                  key={`${row.order_id}-${row.product_id}`}
                  hover
                  sx={{
                    bgcolor: rowBg,
                    "&:hover": { bgcolor: "#F0F3FA !important" },
                  }}
                >
                  {columnsWithMeta.map((column) => (
                    <AppTableRowCell
                      key={column.key}
                      sx={{
                        ...bodyCellSx,
                        ...sectionBorderSx(column.isSectionEnd),
                        width: column.width,
                        minWidth: column.width,
                        maxWidth: column.width,
                        textAlign: column.align,
                        bgcolor: rowBg,
                        whiteSpace: column.wrap ? "normal" : "nowrap",
                        wordBreak: column.wrap ? "break-word" : "normal",
                      }}
                    >
                      {getCellValue(row, column.key)}
                    </AppTableRowCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <AppTablePagenation
        pageNo={page_no}
        limit={limit}
        totalCount={total_count}
        onPageChange={onPageChange}
        handleRowsChange={onRowsPerPageChange}
      />
    </Paper>
  );
};

export default OrderPipelineTable;
